// src/Dashboard/pages/teacher/AttendanceTable/hooks/useAttendanceTableDashboard.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { getGroupsList } from "../../../../../services/groupServices";
import { getAdherentsByGroup } from "../../../../../services/adherentServices";
import { getSettings } from "../../../../../services/settingsServices";
import {
  getAllAttendances,
  setAttendance as apiSetAttendance,
  clearAttendance as apiClearAttendance,
} from "../../../../../services/attendanceServices";

import {
  buildAttendanceRows,
  generateSeasonMonths,
  getCurrentSeasonMonth,
} from "../utils/attendanceTableUtils";

export function useAttendanceTableDashboard() {
  const [groups, setGroups] = useState([]);
  const [months, setMonths] = useState([]);

  const [selectedGroupId, setSelectedGroupIdState] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");

  const [adherents, setAdherents] = useState([]);
  const [allAttendances, setAllAttendances] = useState(null);
  const [tableRows, setTableRows] = useState([]);

  const [seasonActive, setSeasonActive] = useState("");

  const [loadingGroups, setLoadingGroups] = useState(true);
  const [loadingAdherents, setLoadingAdherents] = useState(false);
  const [loadingAttendances, setLoadingAttendances] = useState(false);

  const loading = loadingGroups || loadingAdherents || loadingAttendances;

  const selectedGroup = useMemo(() => {
    return groups.find((group) => String(group.id) === String(selectedGroupId));
  }, [groups, selectedGroupId]);

  const loadInitialData = useCallback(async () => {
    try {
      setLoadingGroups(true);

      const [groupList, settingsData] = await Promise.all([
        getGroupsList(),
        getSettings(),
      ]);

      const safeGroups = (Array.isArray(groupList) ? groupList : [])
        .slice()
        .sort((a, b) =>
          String(a?.nom || "").localeCompare(String(b?.nom || ""), "fr"),
        );

      const settings = Array.isArray(settingsData)
        ? settingsData[0]
        : settingsData;

      const season = settings?.saison_active || "";
      const seasonMonths = generateSeasonMonths(season);
      const currentMonth = getCurrentSeasonMonth(seasonMonths);

      setGroups(safeGroups);
      setSeasonActive(season);
      setMonths(seasonMonths);
      setSelectedMonth(currentMonth || seasonMonths?.[0]?.value || "");
    } catch (error) {
      console.error("Erreur chargement tableau présences :", error);
      toast.error("Erreur lors du chargement des groupes.");
    } finally {
      setLoadingGroups(false);
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const setSelectedGroupId = useCallback((groupId) => {
    setSelectedGroupIdState(groupId ? String(groupId) : "");
    setTableRows([]);
  }, []);

  const loadAdherents = useCallback(async () => {
    if (!selectedGroupId) {
      setAdherents([]);
      setTableRows([]);
      return;
    }

    try {
      setLoadingAdherents(true);

      const list = await getAdherentsByGroup(selectedGroupId);

      const safeList = (Array.isArray(list) ? list : []).slice().sort(
        (a, b) =>
          String(a?.nom || "").localeCompare(String(b?.nom || ""), "fr", {
            sensitivity: "base",
          }) ||
          String(a?.prenom || "").localeCompare(String(b?.prenom || ""), "fr", {
            sensitivity: "base",
          }),
      );

      setAdherents(safeList);
    } catch (error) {
      console.error("Erreur chargement adhérents du groupe :", error);
      toast.error("Erreur lors du chargement des adhérents du groupe.");
      setAdherents([]);
    } finally {
      setLoadingAdherents(false);
    }
  }, [selectedGroupId]);

  useEffect(() => {
    loadAdherents();
  }, [loadAdherents]);

  const ensureAttendancesLoaded = useCallback(async () => {
    if (allAttendances) {
      return allAttendances;
    }

    try {
      setLoadingAttendances(true);

      const data = await getAllAttendances();
      const safeData = Array.isArray(data) ? data : [];

      setAllAttendances(safeData);

      return safeData;
    } catch (error) {
      console.error("Erreur chargement présences :", error);
      toast.error("Erreur lors du chargement des présences.");
      return [];
    } finally {
      setLoadingAttendances(false);
    }
  }, [allAttendances]);

  const computeRows = useCallback(
    async ({ forceAttendances } = {}) => {
      if (!selectedGroupId || !selectedMonth || adherents.length === 0) {
        setTableRows([]);
        return;
      }

      const attendances = forceAttendances || (await ensureAttendancesLoaded());

      setTableRows(buildAttendanceRows(selectedMonth, adherents, attendances));
    },
    [selectedGroupId, selectedMonth, adherents, ensureAttendancesLoaded],
  );

  useEffect(() => {
    computeRows();
  }, [computeRows]);

  const kpis = useMemo(() => {
    const presents = tableRows.reduce(
      (sum, row) => sum + Number(row.totalPresent || 0),
      0,
    );

    const absents = tableRows.reduce(
      (sum, row) => sum + Number(row.totalAbsent || 0),
      0,
    );

    const totalMarked = presents + absents;
    const rate =
      totalMarked > 0 ? Math.round((presents / totalMarked) * 100) : 0;

    const bestPresent = tableRows.length
      ? Math.max(...tableRows.map((row) => Number(row.totalPresent || 0)))
      : 0;

    const bestCount =
      bestPresent > 0
        ? tableRows.filter(
            (row) => Number(row.totalPresent || 0) === bestPresent,
          ).length
        : 0;

    return {
      presents,
      absents,
      totalMarked,
      rate,
      bestPresent,
      bestCount,
    };
  }, [tableRows]);

  const highlightIds = useMemo(() => {
    if (!tableRows.length) return [];

    const maxPresent = Math.max(
      ...tableRows.map((row) => Number(row.totalPresent || 0)),
    );

    if (maxPresent <= 0) return [];

    return tableRows
      .filter((row) => Number(row.totalPresent || 0) === maxPresent)
      .map((row) => row.id);
  }, [tableRows]);

  const handleToggleCell = useCallback(
    async (adherentId, day, currentValue) => {
      if (!selectedMonth) return;

      const dateISO = `${selectedMonth}-${day}`;

      const nextState =
        currentValue === true
          ? "ABSENT"
          : currentValue === false
            ? "CLEAR"
            : "PRESENT";

      const previousAttendances = allAttendances || [];
      const matchesCell = (attendance) =>
        Number(attendance.adherent_id) === Number(adherentId) &&
        String(attendance.date_presence).slice(0, 10) === dateISO;

      let optimisticAttendances = [];

      if (nextState === "PRESENT") {
        optimisticAttendances = [
          ...previousAttendances.filter(
            (attendance) => !matchesCell(attendance),
          ),
          {
            adherent_id: adherentId,
            presence: true,
            date_presence: dateISO,
          },
        ];
      } else if (nextState === "ABSENT") {
        optimisticAttendances = [
          ...previousAttendances.filter(
            (attendance) => !matchesCell(attendance),
          ),
          {
            adherent_id: adherentId,
            presence: false,
            date_presence: dateISO,
          },
        ];
      } else {
        optimisticAttendances = previousAttendances.filter(
          (attendance) => !matchesCell(attendance),
        );
      }

      setAllAttendances(optimisticAttendances);
      setTableRows(
        buildAttendanceRows(selectedMonth, adherents, optimisticAttendances),
      );

      try {
        if (nextState === "PRESENT") {
          await apiSetAttendance(adherentId, dateISO, true);
        } else if (nextState === "ABSENT") {
          await apiSetAttendance(adherentId, dateISO, false);
        } else {
          await apiClearAttendance(adherentId, dateISO);
        }
      } catch (error) {
        console.error("Erreur mise à jour présence :", error);
        toast.error("Échec de la mise à jour de la case.");

        setAllAttendances(previousAttendances);
        setTableRows(
          buildAttendanceRows(selectedMonth, adherents, previousAttendances),
        );
      }
    },
    [selectedMonth, allAttendances, adherents],
  );

  const refresh = useCallback(async () => {
    await loadInitialData();

    if (selectedGroupId) {
      await loadAdherents();
    }

    try {
      setLoadingAttendances(true);

      const data = await getAllAttendances();
      const safeData = Array.isArray(data) ? data : [];

      setAllAttendances(safeData);
      setTableRows(buildAttendanceRows(selectedMonth, adherents, safeData));
    } catch (error) {
      console.error("Erreur actualisation présences :", error);
      toast.error("Erreur lors de l’actualisation.");
    } finally {
      setLoadingAttendances(false);
    }
  }, [
    loadInitialData,
    selectedGroupId,
    loadAdherents,
    selectedMonth,
    adherents,
  ]);

  return {
    groups,
    months,

    selectedGroupId,
    selectedGroup,
    selectedMonth,

    adherents,
    allAttendances,
    tableRows,
    seasonActive,

    loading,
    loadingGroups,
    loadingAdherents,

    kpis,
    highlightIds,

    setSelectedGroupId,
    setSelectedMonth,
    setAllAttendances,

    handleToggleCell,
    refresh,
  };
}

export default useAttendanceTableDashboard;
